import { useState, useRef, useEffect, useCallback, RefObject } from 'react';

export interface LayoutConfig {
  defaultLeft: number;
  defaultRight: number;
  minLeft: number;
  minCenter: number;
  minRight: number;
}

export function useThreeColumnLayout(
  containerRef: RefObject<HTMLElement>,
  config: LayoutConfig
) {
  const { defaultLeft, defaultRight, minLeft, minCenter, minRight } = config;

  const [leftWidth, setLeftWidth] = useState(() => {
    if (typeof window === 'undefined') return defaultLeft;
    const saved = localStorage.getItem('split-layout-left');
    return saved ? parseInt(saved, 10) : defaultLeft;
  });

  const [rightWidth, setRightWidth] = useState(() => {
    if (typeof window === 'undefined') return defaultRight;
    const saved = localStorage.getItem('split-layout-right');
    return saved ? parseInt(saved, 10) : defaultRight;
  });

  const [canvasTargetWidth, setCanvasTargetWidth] = useState<number | 'full'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('form-builder-canvas-width');
      if (saved) return saved === 'full' ? 'full' : parseInt(saved, 10);
    }
    return 1024;
  });

  const [dragging, setDragging] = useState<'left' | 'right' | 'canvas-left' | 'canvas-right' | null>(null);
  const [showPulse, setShowPulse] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const discovered = localStorage.getItem('form-builder-resize-discovered');
      if (!discovered) setShowPulse(true);
    }
  }, []);

  const leftElRef = useRef<HTMLDivElement>(null);
  const rightElRef = useRef<HTMLDivElement>(null);
  const centerColRef = useRef<HTMLDivElement>(null);
  const canvasCardRef = useRef<HTMLDivElement>(null);
  const canvasBadgeRef = useRef<HTMLDivElement>(null);

  const stateRef = useRef({ leftWidth, rightWidth, canvasTargetWidth });
  useEffect(() => {
    stateRef.current = { leftWidth, rightWidth, canvasTargetWidth };
  }, [leftWidth, rightWidth, canvasTargetWidth]);

  const applyCanvasPx = useCallback((px: number) => {
    if (canvasCardRef.current) {
      canvasCardRef.current.style.width = `${px}px`;
      canvasCardRef.current.style.maxWidth = `${px}px`;
    }
    if (canvasBadgeRef.current) canvasBadgeRef.current.textContent = `${Math.round(px)}px`;
  }, []);

  const validateLayout = useCallback(() => {
    if (!containerRef.current || window.innerWidth < 1280) {
      if (centerColRef.current) {
        const cs = window.getComputedStyle(centerColRef.current);
        const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
        const centerWidth = centerColRef.current.clientWidth - paddingX;
        
        let target = stateRef.current.canvasTargetWidth;
        if (target === 'full') target = centerWidth;
        const actual = Math.min(target, centerWidth);
        applyCanvasPx(actual);
      }
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    let newLeft = stateRef.current.leftWidth;
    let newRight = stateRef.current.rightWidth;

    if (newLeft < minLeft) newLeft = minLeft;
    if (newRight < minRight) newRight = minRight;

    let center = containerWidth - newLeft - newRight;
    if (center < minCenter) {
      const excess = minCenter - center;
      const rightCanShrink = Math.max(0, newRight - minRight);
      const rightShrink = Math.min(excess, rightCanShrink);
      newRight -= rightShrink;
      
      const remainingExcess = excess - rightShrink;
      if (remainingExcess > 0) {
        newLeft = Math.max(minLeft, newLeft - remainingExcess);
      }
      center = containerWidth - newLeft - newRight;
    }

    if (newLeft !== stateRef.current.leftWidth) setLeftWidth(newLeft);
    if (newRight !== stateRef.current.rightWidth) setRightWidth(newRight);
    
    if (centerColRef.current) {
      const cs = window.getComputedStyle(centerColRef.current);
      const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const availableCenter = center - paddingX;
      
      let target = stateRef.current.canvasTargetWidth;
      if (target === 'full') target = availableCenter;
      
      const actualCanvas = Math.max(0, Math.min(target, availableCenter));
      applyCanvasPx(actualCanvas);
    }
  }, [containerRef, minLeft, minRight, minCenter, applyCanvasPx]);

  useEffect(() => {
    if (!containerRef.current) return;
    validateLayout();

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(validateLayout);
    });
    
    observer.observe(containerRef.current);
    if (centerColRef.current) {
      observer.observe(centerColRef.current);
    }
    
    return () => observer.disconnect();
  }, [validateLayout, containerRef]);

  const updateCanvasWidth = useCallback((newWidth: number | 'full') => {
    setCanvasTargetWidth(newWidth);
    localStorage.setItem('form-builder-canvas-width', newWidth.toString());
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent, handleType: 'left' | 'right' | 'canvas-left' | 'canvas-right') => {
    if (e.button !== 0) return;
    
    if ((handleType === 'left' || handleType === 'right') && window.innerWidth < 1280) return;
    
    e.preventDefault();
    e.stopPropagation();

    localStorage.setItem('form-builder-resize-discovered', 'true');
    setShowPulse(false);
    
    setDragging(handleType);

    const originalUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const startX = e.clientX;
    const startLeft = stateRef.current.leftWidth;
    const startRight = stateRef.current.rightWidth;
    let currentCanvasTarget = stateRef.current.canvasTargetWidth;
    const startCanvasTargetNum = currentCanvasTarget === 'full' ? (canvasCardRef.current?.clientWidth ?? 1024) : currentCanvasTarget;

    let currentLeft = startLeft;
    let currentRight = startRight;
    let currentActualCanvas = startCanvasTargetNum;
    let frameQueued = false;

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.clientWidth;
      const deltaX = moveEvent.clientX - startX;

      if (handleType === 'left' || handleType === 'right') {
        if (!leftElRef.current || !rightElRef.current) return;
        if (handleType === 'left') {
          currentLeft = startLeft + deltaX;
          if (currentLeft < minLeft) currentLeft = minLeft;

          const maxLeftGivenRightMin = containerWidth - minCenter - minRight;
          if (currentLeft > maxLeftGivenRightMin) {
            currentLeft = maxLeftGivenRightMin;
            currentRight = minRight;
          } else {
            const potentialCenter = containerWidth - currentLeft - startRight;
            if (potentialCenter < minCenter) {
              currentRight = startRight - (minCenter - potentialCenter);
            } else {
              currentRight = startRight;
            }
          }
        } else {
          currentRight = startRight - deltaX;
          if (currentRight < minRight) currentRight = minRight;
          
          const maxRightGivenLeftMin = containerWidth - minCenter - minLeft;
          if (currentRight > maxRightGivenLeftMin) {
            currentRight = maxRightGivenLeftMin;
            currentLeft = minLeft;
          } else {
            const potentialCenter = containerWidth - startLeft - currentRight;
            if (potentialCenter < minCenter) {
              currentLeft = startLeft - (minCenter - potentialCenter);
            } else {
              currentLeft = startLeft;
            }
          }
        }

        leftElRef.current.style.setProperty('--panel-width', `${currentLeft}px`);
        rightElRef.current.style.setProperty('--panel-width', `${currentRight}px`);

        if (centerColRef.current) {
          const cs = window.getComputedStyle(centerColRef.current);
          const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
          const currentCenterWidth = containerWidth - currentLeft - currentRight - paddingX;
          const target = stateRef.current.canvasTargetWidth === 'full' ? currentCenterWidth : stateRef.current.canvasTargetWidth;
          currentActualCanvas = Math.max(0, Math.min(target, currentCenterWidth));
          applyCanvasPx(currentActualCanvas);
        }
      } 
      else {
        if (!centerColRef.current) return;
        const widthDelta = handleType === 'canvas-right' ? deltaX * 2 : -deltaX * 2;
        
        const cs = window.getComputedStyle(centerColRef.current);
        const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
        const currentCenterWidth = centerColRef.current.clientWidth - paddingX;

        currentActualCanvas = Math.max(0, Math.min(Math.max(startCanvasTargetNum + widthDelta, 320), currentCenterWidth));
        if (!frameQueued) {
          frameQueued = true;
          requestAnimationFrame(() => {
            frameQueued = false;
            applyCanvasPx(currentActualCanvas);
          });
        }
      }
    };

    const onPointerUp = () => {
      document.body.style.userSelect = originalUserSelect;
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      
      setDragging(null);

      if (handleType === 'left' || handleType === 'right') {
        setLeftWidth(currentLeft);
        setRightWidth(currentRight);
        localStorage.setItem('split-layout-left', currentLeft.toString());
        localStorage.setItem('split-layout-right', currentRight.toString());
        
        if (leftElRef.current) leftElRef.current.style.removeProperty('--panel-width');
        if (rightElRef.current) rightElRef.current.style.removeProperty('--panel-width');
      } else {
        let finalCanvas = currentActualCanvas;
        if (centerColRef.current) {
          const cs = window.getComputedStyle(centerColRef.current);
          const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
          const currentCenterWidth = centerColRef.current.clientWidth - paddingX;

          for (const snap of [640, 768, 1024]) {
            if (Math.abs(finalCanvas - snap) < 20) { finalCanvas = snap; break; }
          }
          
          if (finalCanvas >= currentCenterWidth - 20) {
            updateCanvasWidth('full');
          } else {
            updateCanvasWidth(finalCanvas);
          }
        }
      }
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }, [minLeft, minRight, minCenter, applyCanvasPx, updateCanvasWidth, containerRef]);

  const resetSplit = useCallback((side: 'left' | 'right') => {
    if (side === 'left') {
      setLeftWidth(defaultLeft);
      localStorage.setItem('split-layout-left', defaultLeft.toString());
    } else {
      setRightWidth(defaultRight);
      localStorage.setItem('split-layout-right', defaultRight.toString());
    }
  }, [defaultLeft, defaultRight]);

  return {
    splitLeftWidth: leftWidth,
    splitRightWidth: rightWidth,
    canvasTargetWidth,
    dragging,
    showPulse,
    leftElRef,
    rightElRef,
    centerColRef,
    canvasCardRef,
    canvasBadgeRef,
    onPointerDown,
    resetSplit,
    updateCanvasWidth
  };
}
